import React, { useState, useEffect } from 'react';
import './PhoneInput.css';
import {
  COUNTRIES,
  normalizeE164,
  getCountryByDialCodeOrPhone,
  getExpectedNationalNumberLength,
  formatE164ForDisplay,
} from '../utils/phoneFormat';

// Re-exported for backwards compatibility — the actual implementations now
// live in src/utils/phoneFormat.js so any page can reuse them without
// importing this input component. See src/utils/phoneFormat.js.
export {
  COUNTRIES,
  normalizeE164,
  getCountryByDialCodeOrPhone,
  getExpectedNationalNumberLength,
  formatE164ForDisplay,
};

function PhoneInput({ value = '', onChange, placeholder, required = false, disabled = false, className = '' }) {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]); // Default to Singapore
  const [phoneNumber, setPhoneNumber] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown && !event.target.closest('.country-code-dropdown-wrapper')) {
        setShowCountryDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCountryDropdown]);

  // Parse existing value on mount or when value prop changes
  useEffect(() => {
    if (value) {
      // Check if value starts with a country code
      const matchingCountry = COUNTRIES.find(country => value.startsWith(country.dialCode));
      if (matchingCountry) {
        setSelectedCountry(matchingCountry);
        const numberPart = value.replace(matchingCountry.dialCode, '').trim();
        setPhoneNumber(numberPart.replace(/\D/g, ''));
        formatPhoneNumber(numberPart.replace(/\D/g, ''), matchingCountry);
      } else {
        // Try to detect Singapore number (default)
        const digits = value.replace(/\D/g, '');
        if (digits.startsWith('65')) {
          setSelectedCountry(COUNTRIES[0]);
          setPhoneNumber(digits.substring(2));
          formatPhoneNumber(digits.substring(2), COUNTRIES[0]);
        } else if (value.startsWith('+')) {
          // Try to match any country code
          const found = COUNTRIES.find(c => value.startsWith(c.dialCode));
          if (found) {
            setSelectedCountry(found);
            const numberPart = value.replace(found.dialCode, '').trim();
            setPhoneNumber(numberPart.replace(/\D/g, ''));
            formatPhoneNumber(numberPart.replace(/\D/g, ''), found);
          } else {
            // Assume Singapore format
            setPhoneNumber(digits);
            formatPhoneNumber(digits, COUNTRIES[0]);
          }
        } else {
          // Assume it's just the number part, use selected country
          const digits = value.replace(/\D/g, '');
          setPhoneNumber(digits);
          formatPhoneNumber(digits, selectedCountry);
        }
      }
    } else {
      setPhoneNumber('');
      setDisplayValue('');
    }
  }, [value]);

  const formatPhoneNumber = (digits, country) => {
    if (!digits) {
      setDisplayValue('');
      return;
    }

    const format = country.format;
    let formatted = '';
    let digitIndex = 0;

    for (let i = 0; i < format.length && digitIndex < digits.length; i++) {
      if (i > 0) formatted += ' ';
      const segmentLength = format[i];
      const segment = digits.substring(digitIndex, digitIndex + segmentLength);
      formatted += segment;
      digitIndex += segmentLength;
    }

    // Add any remaining digits
    if (digitIndex < digits.length) {
      formatted += ' ' + digits.substring(digitIndex);
    }

    setDisplayValue(formatted);
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
    formatPhoneNumber(phoneNumber, country);
    // Notify parent of full phone number change
    const fullNumber = phoneNumber ? `${country.dialCode}${phoneNumber}` : '';
    if (onChange) {
      onChange({ target: { value: fullNumber, name: 'phoneNumber' } });
    }
  };

  const handlePhoneChange = (e) => {
    const input = e.target.value;
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');
    
    // Limit to reasonable length (15 digits max for international numbers)
    const limitedDigits = digits.substring(0, 15);
    
    setPhoneNumber(limitedDigits);
    formatPhoneNumber(limitedDigits, selectedCountry);
    
    // Notify parent of full phone number change
    const fullNumber = limitedDigits ? `${selectedCountry.dialCode}${limitedDigits}` : '';
    if (onChange) {
      onChange({ target: { value: fullNumber, name: 'phoneNumber' } });
    }
  };

  const getFullPhoneNumber = () => {
    return phoneNumber ? `${selectedCountry.dialCode}${phoneNumber}` : '';
  };

  return (
    <div className={`phone-input-container ${className}`}>
      <div className="phone-input-wrapper">
        <div className="country-code-dropdown-wrapper">
          <div
            className={`country-code-display-trigger ${disabled ? 'disabled' : ''}`}
            onClick={() => !disabled && setShowCountryDropdown(!showCountryDropdown)}
          >
            <span className="country-code-value">{selectedCountry.dialCode} {selectedCountry.name}</span>
            {!disabled && <span className="dropdown-arrow">▼</span>}
          </div>
          {showCountryDropdown && !disabled && (
            <div className="country-code-dropdown">
              {COUNTRIES.map(country => (
                <div
                  key={country.code}
                  className={`country-code-dropdown-item ${selectedCountry.code === country.code ? 'selected' : ''}`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className="country-dial-code">{country.dialCode}</span>
                  <span className="country-name">{country.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <input
          type="tel"
          className="phone-number-input"
          value={displayValue}
          onChange={handlePhoneChange}
          placeholder={placeholder || selectedCountry.placeholder}
          required={required}
          disabled={disabled}
        />
      </div>
      <input
        type="hidden"
        name="phoneNumber"
        value={getFullPhoneNumber()}
      />
    </div>
  );
}

export default PhoneInput;

